<app-confirm-dialog
  #confirmModal
  [title]="'Are you sure to reset questions?'"
  [message]="'The previous questions will not be visible in your future online meetings. This will load the default questions.'"
  (confirmed)="handleResetQuestions($event)"
></app-confirm-dialog>

<app-confirm-dialog
  #deleteQuestionModal
  [title]="'Delete this question?'"
  [message]="deleteQuestionMessage"
  (confirmed)="handleDeleteQuestion($event)"
></app-confirm-dialog>

<app-confirm-dialog
  #deleteTemplateModal
  [title]="'Delete this template?'"
  [message]="'All questions in this template will be permanently deleted. This cannot be undone.'"
  (confirmed)="handleDeleteTemplate($event)"
></app-confirm-dialog>

<app-main-wrapper title="Questions">
  <div class="questions-page">

    <!-- ── Template Tabs ──────────────────────────────────────────────── -->
    <div class="template-tabs" *ngIf="templatesEnabled">
      <div
        *ngFor="let t of templates; let i = index"
        class="template-tab"
        [class.active]="t.id === currentTemplateId"
        [class.dragging]="dragSourceIndex === i"
        [class.drag-over]="dragOverIndex === i && dragSourceIndex !== i"
        [draggable]="templates.length > 1 && renamingTemplateId !== t.id"
        (click)="switchTemplate(t.id)"
        (dragstart)="onTabDragStart(i)"
        (dragover)="onTabDragOver(i, $event)"
        (drop)="onTabDrop(i, $event)"
        (dragend)="onTabDragEnd()"
      >
        <input
          *ngIf="renamingTemplateId === t.id"
          class="tab-rename-input"
          [(ngModel)]="renameValue"
          [ngModelOptions]="{standalone: true}"
          (blur)="saveRename()"
          (keydown)="onRenameKeydown($event)"
          (click)="$event.stopPropagation()"
          autofocus
        />
        <span
          *ngIf="renamingTemplateId !== t.id"
          (dblclick)="startRename(t, $event)"
        >{{ t.name }}</span>
        <button
          *ngIf="templates.length > 1 && renamingTemplateId !== t.id"
          type="button"
          class="tab-close"
          (click)="confirmDeleteTemplate(t.id, $event)"
          title="Delete template"
        >
          <fa-icon [icon]="faTimes"></fa-icon>
        </button>
      </div>
      <button
        type="button"
        class="add-template-btn"
        [class.empty]="templates.length === 0"
        (click)="openNewTemplateModal()"
        title="Add template"
      >
        <fa-icon [icon]="faPlus" class="add-template-icon"></fa-icon>
        <span class="add-template-label" *ngIf="templates.length === 0">Add Template</span>
        <span class="add-template-new" *ngIf="templates.length > 0">New</span>
      </button>
    </div>


    <!-- ── AI Question Generator ─────────────────────────────────────────── -->
    <div class="ai-generator">
      <div class="ai-prompt-row">
        <div class="ai-input-wrapper">
          <textarea
            [(ngModel)]="aiPrompt"
            [ngModelOptions]="{standalone: true}"
            class="ai-textarea"
            rows="2"
            placeholder="Generate BANT questions for enterprise SaaS or paste your current questions set."
            [disabled]="isGenerating"
          ></textarea>
        </div>
        <button
          type="button"
          class="ai-generate-btn"
          (click)="generateQuestions()"
          [disabled]="isGenerating || !aiPrompt.trim()"
        >
          <span class="sparkle-1"></span>
          <span class="sparkle-2"></span>
          <span class="sparkle-3"></span>
          <span *ngIf="isGenerating" class="spinner-sm"></span>
          <fa-icon *ngIf="!isGenerating" [icon]="faWandMagicSparkles"></fa-icon>
          {{ isGenerating ? 'Generating...' : 'Generate' }}
        </button>
      </div>
      <div class="ai-suggestions">
        <button type="button" class="ai-chip" (click)="setAiPrompt('Generate BANT questions for enterprise SaaS')">BANT Questions</button>
        <button type="button" class="ai-chip" (click)="setAiPrompt('Create MEDDPICC questions for a discovery call')">MEDDPICC Questions</button>
        <button type="button" class="ai-chip" (click)="setAiPrompt('Generate qualification questions for a 15-minute call')">Short Call Questions</button>
      </div>
    </div>

    <!-- View Mode -->
    <div *ngIf="!isEditMode" class="questions-container">
      <div
        *ngIf="savedQuestions.length === 0"
        class="empty-state"
        role="button"
        tabindex="0"
        aria-label="Add meeting questions"
        (click)="startCustomQuestions()"
        (keydown.enter)="startCustomQuestions($event)"
        (keydown.space)="startCustomQuestions($event)"
      >
        <fa-icon [icon]="faPlus" class="empty-icon"></fa-icon>
        <p class="empty-title">No questions configured yet</p>
        <p class="empty-subtitle">Click below to add your meeting questions.</p>
        <div class="empty-actions">
          <button type="button" class="empty-btn primary" (click)="startCustomQuestions($event)">
            Add questions
          </button>
          <button type="button" class="empty-btn secondary" (click)="loadDefaultQuestionSet($event)">
            Load default questions
          </button>
        </div>
      </div>

      <div class="question-list" *ngIf="savedQuestions.length > 0">
        <div
          *ngFor="let question of savedQuestions; let i = index"
          class="question-item"
          [class.published]="isPublished"
        >
          <div class="question-content">
            <div class="question-number">{{ i + 1 }}.</div>
            <div class="question-text">{{ question.question_text }}</div>
          </div>
        </div>
      </div>

      <div class="bottom-actions" *ngIf="savedQuestions.length > 0">
        <button
          type="button"
          class="action-btn primary edit-questions-btn"
          (click)="toggleEditMode()"
        >
          Edit Questions
        </button>

        <button
          type="button"
          class="action-btn secondary"
          (click)="resetQuestions()"
        >
          Reset Questions
        </button>
      </div>
    </div>

    <!-- Edit Mode -->
    <form *ngIf="isEditMode" [formGroup]="questionForm" (ngSubmit)="submit()" class="edit-form">
      <div 
        class="questions-container editing" 
        formArrayName="questions"
        cdkDropList 
        (cdkDropListDropped)="onQuestionDrop($event)"
      >
        <div 
          *ngFor="let question of questions.controls; let i = index" 
          [formGroupName]="i" 
          cdkDrag
          class="question-item editable">
          <div class="question-content">
            <button 
              type="button" 
              class="drag-handle"
              title="Drag to reorder"
              cdkDragHandle
            >
              <fa-icon [icon]="faGripVertical"></fa-icon>
            </button>
            <div class="question-number">{{ i + 1 }}.</div>
            <input
              type="text"
              formControlName="question_text"
              class="question-input"
              placeholder="Enter your question"
            />
            <div class="question-actions">
              <button
                type="button"
                class="action-btn add-btn"
                (click)="addMore(i)"
                title="Add question after this one"
              >
                <fa-icon [icon]="faPlus"></fa-icon>
              </button>
              <button
                type="button"
                class="action-btn delete-btn"
                (click)="removeQuestion(i)"
                [disabled]="questions.length <= 1"
                [title]="questions.length <= 1 ? 'Cannot delete the last question' : 'Delete this question'"
              >
                <fa-icon [icon]="faTrash"></fa-icon>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="form-actions">
        <button 
          type="submit" 
          class="action-btn primary publish-questions-btn"
          [disabled]="isRequestPending || !questionForm.valid"
        >
          {{ isRequestPending ? 'Publishing...' : 'Publish Questions' }}
          <span *ngIf="questionsToDelete.length > 0" class="delete-badge">
            {{ questionsToDelete.length }} to delete
          </span>
        </button>

        <button
          type="button"
          class="action-btn secondary"
          (click)="resetQuestions()"
        >
          Reset Questions
        </button>

        <button
          type="button"
          class="action-btn secondary"
          (click)="toggleEditMode()"
        >
          Cancel
        </button>
      </div>
    </form>
  </div>
</app-main-wrapper>

<!-- ── New Template Modal ───────────────────────────────────────────────── -->
<div class="modal-overlay" [class.visible]="showNewTemplateModal" (click)="closeNewTemplateModal()">
  <div class="modal-content" (click)="$event.stopPropagation()">
    <div class="modal-header">
      <h3 class="modal-title">Create New Template</h3>
      <button type="button" class="modal-close-btn" (click)="closeNewTemplateModal()">
        <fa-icon [icon]="faTimes"></fa-icon>
      </button>
    </div>
    <div class="modal-body">
      <div class="modal-form-group">
        <label class="modal-label">Template Name</label>
        <input
          type="text"
          class="modal-input"
          [(ngModel)]="newTemplateName"
          [ngModelOptions]="{standalone: true}"
          placeholder="e.g., Enterprise SaaS Questions"
          (keydown.enter)="createTemplate()"
          autofocus
        />
      </div>
    </div>
    <div class="modal-footer">
      <button type="button" class="action-btn secondary" (click)="closeNewTemplateModal()">Cancel</button>
      <button
        type="button"
        class="action-btn primary"
        (click)="createTemplate()"
        [disabled]="!newTemplateName.trim()"
      >
        Create &amp; Switch
      </button>
    </div>
  </div>
</div>
